import React from "react";
import { Ship, Calendar, MapPin, ChevronRight } from "lucide-react";
import { useCruise } from "../context/CruiseContext";
import { Button } from "./ui/button";
import Countdown from "./Countdown";
import { useLocation } from "react-router-dom";
import { seo } from "../seo/schema";

const Hero = () => {
  const { EVENT, HERO_IMAGE, hero } = useCruise();
  const route = useLocation().pathname.replace(/\/+$/, "");
  const h1Detail = seo.pages[route]?.h1Detail;
  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt={`${EVENT.ship} cruise ship — House Heads At Sea ${EVENT.year}`} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/60" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 w-full pt-44 sm:pt-52 pb-16">
        <div className="max-w-3xl animate-fadeUp">
          <div className="inline-flex items-center gap-2 bg-[#E50914] rounded-full px-4 py-1.5 mb-6">
            <span className="font-cond uppercase text-xs tracking-[0.25em] text-white font-semibold">
              {EVENT.edition}
            </span>
          </div>

          <h1 className="font-display text-white uppercase leading-[0.9] text-5xl sm:text-7xl lg:text-8xl">
            House <span className="text-[#E50914]">Heads</span>{" "}
            <br />
            At Sea <span className="text-[#E50914]">{EVENT.year}</span>
            {h1Detail && <span className="sr-only"> — {h1Detail}</span>}
          </h1>

          <div className="mt-3 flex items-center gap-3">
            <span className="section-line" />
            <p className="font-cond uppercase tracking-[0.4em] text-[var(--hh-gold)] text-lg sm:text-2xl">
              {EVENT.tagline}
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4">
            <div className="flex items-center gap-3">
              <Calendar className="text-[#E50914]" size={22} />
              <div>
                <p className="font-cond uppercase text-xs text-white/50 tracking-widest">Setting Sail</p>
                <p className="font-cond font-semibold text-white text-lg">{EVENT.dateShort}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Ship className="text-[#E50914]" size={22} />
              <div>
                <p className="font-cond uppercase text-xs text-white/50 tracking-widest">Aboard</p>
                <p className="font-cond font-semibold text-white text-lg">{EVENT.ship}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="text-[#E50914]" size={22} />
              <div>
                <p className="font-cond uppercase text-xs text-white/50 tracking-widest">Leaving Out Of</p>
                <p className="font-cond font-semibold text-white text-lg">{EVENT.departurePort}</p>
              </div>
            </div>
          </div>

          <p className="mt-6 text-white/70 text-base sm:text-lg max-w-xl">
            {hero.paragraph}
          </p>

          <div className="mt-8">
            <Countdown target={hero.countdownTarget} />
          </div>

          <div className="mt-9 flex flex-wrap gap-4">
            <a href={EVENT.bookingUrl} target="_blank" rel="noopener noreferrer">
              <Button className="group bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest text-base rounded-full px-8 py-6 h-auto">
                Secure Your Cabin
                <ChevronRight className="ml-1 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </a>
            <a href="#itinerary">
              <Button
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white hover:text-[#0A0A0A] font-cond uppercase tracking-widest text-base rounded-full px-8 py-6 h-auto"
              >
                View Itinerary
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom marquee strip — seamless scroll */}
      <div className="absolute bottom-0 left-0 w-full bg-[#E50914] py-2.5 overflow-hidden z-10">
        <div className="flex w-max animate-marquee">
          {[0, 1].map((dup) => (
            <div
              key={dup}
              className="flex items-center whitespace-nowrap font-cond uppercase tracking-[0.3em] text-white text-sm"
              aria-hidden={dup === 1}
            >
              {[...hero.marquee, ...hero.marquee, ...hero.marquee].map((m, i) => (
                <span key={i} className="flex items-center">
                  <span className="mx-6">{m}</span>
                  <span style={{ color: "var(--hh-gold)" }}>◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
